import { alpha, useTheme } from '@mui/material';

// Same look as the user dialogs (components/users/Add.js, Edit.js)
const useDialogStyles = () => {
  const theme = useTheme();

  const isDark = theme.palette.mode === 'dark';
  const accent = theme.palette.secondary.main;
  const accentText = theme.palette.getContrastText(accent);
  const border = `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : theme.palette.neutral[200]}`;

  // Scrollbar like the DataGrids (useGridStyles): transparent track, slim rounded handle, gold on
  // hover. Firefox only gets the standard properties: in Chrome/Edge 121+ they would switch off
  // the ::-webkit-scrollbar rules.
  const scrollThumb = isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.22)';
  const scrollbarSx = {
    '&::-webkit-scrollbar': { width: 10, height: 10, backgroundColor: 'transparent' },
    '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: scrollThumb,
      borderRadius: 10,
      border: '3px solid transparent',
      backgroundClip: 'content-box',
      minHeight: 40,
    },
    '&::-webkit-scrollbar-thumb:hover': { backgroundColor: alpha(accent, 0.75), border: '2px solid transparent' },
    '&::-webkit-scrollbar-thumb:active': { backgroundColor: accent },
    '&::-webkit-scrollbar-corner': { backgroundColor: 'transparent' },
    '&::-webkit-scrollbar-button': { display: 'none' },
    '@supports not selector(::-webkit-scrollbar)': {
      scrollbarWidth: 'thin',
      scrollbarColor: `${scrollThumb} transparent`,
    },
  };

  return {
    scrollbarSx,   // for any other scrolling box inside a dialog

    paperSx: {
      backgroundColor: theme.palette.background.alt,
      color: theme.palette.text.primary,
      borderRadius: 2,
      border,
      boxShadow: 8,
      overflow: 'hidden',
    },

    titleSx: {
      px: 3,
      py: 2,
      fontWeight: 600,
      color: theme.palette.text.primary,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },

    contentSx: {
      px: 3,
      pb: 3,
      // '&&' outranks MUI's "DialogTitle + DialogContent { padding-top: 0 }" rule
      '&&': { pt: 3 },
      backgroundColor: theme.palette.background.alt,
      ...scrollbarSx,
    },

    actionsSx: {
      px: 3,
      py: 1.75,
      gap: 1,
      borderTop: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.alt,
    },

    fieldSx: {
      '& .MuiOutlinedInput-root': {
        color: theme.palette.text.primary,
        backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : theme.palette.background.default,
        '& fieldset': { borderColor: isDark ? 'rgba(255,255,255,0.28)' : theme.palette.neutral[300] },
        '&:hover fieldset': { borderColor: isDark ? 'rgba(255,255,255,0.5)' : theme.palette.neutral[400] },
        '&.Mui-focused fieldset': { borderColor: accent, borderWidth: 1 },
      },
      '& .MuiInputBase-input::placeholder': { color: theme.palette.text.secondary, opacity: 0.8 },
      '& .MuiInputLabel-root': { color: theme.palette.text.secondary },
      '& .MuiInputLabel-root.Mui-focused': { color: accent },
      '& .MuiSelect-icon': { color: theme.palette.text.secondary },
      // The browser paints autofilled fields blue: keep them in the theme
      '& input:-webkit-autofill': {
        WebkitBoxShadow: `0 0 0 100px ${theme.palette.background.alt} inset`,
        WebkitTextFillColor: theme.palette.text.primary,
        caretColor: theme.palette.text.primary,
      },
    },

    menuProps: {
      PaperProps: {
        sx: {
          backgroundColor: theme.palette.background.alt,
          border,
          maxHeight: 320,
          ...scrollbarSx,
        },
      },
    },

    goldButtonSx: {
      backgroundColor: accent,
      color: accentText,
      fontWeight: 700,
      borderRadius: 1.5,
      px: 2.5,
      boxShadow: 'none',
      '&:hover': { backgroundColor: theme.palette.secondary.light, boxShadow: 'none' },
    },

    cancelButtonSx: {
      color: theme.palette.text.primary,
      fontWeight: 600,
      '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
    },
  };
};

export default useDialogStyles;
